import { resolveThemeColors, ThemeColors } from "@citychallenge/common";
import React, { useContext } from "react";

export type { ThemeColors };

// JS view of the per-environment defaults emitted by the central
// resolver in `build-theme.ts`. Anything that needs the *current* theme
// in JS (3D meshes, canvas helpers, charts) should read from
// `useTheme()` rather than these — these only kick in when no upstream
// provider has been mounted.
export const DEFAULT_THEMES = {
  light: resolveThemeColors({ environment: "intractive", mode: "light" }),
  dark: resolveThemeColors({ environment: "intractive", mode: "dark" }),
};

export const ThemeContext = React.createContext<ThemeColors>(
  DEFAULT_THEMES.light
);

export const useTheme = () => {
  return useContext(ThemeContext);
};

const BUILTIN_THEMES = ["default", "light", "dark"];

export function resolveDefaultTheme(
  elementTheme?: string,
  gameDefaultTheme?: string,
  defaultBrandkitId?: string,
  preferredColorTheme?: string
): { brandkitId?: string; themeName: "light" | "dark" } {
  // Element explicitly chose a specific theme (not "default")
  if (elementTheme && elementTheme !== "default") {
    if (!BUILTIN_THEMES.includes(elementTheme))
      return { brandkitId: elementTheme, themeName: "light" };
    if (elementTheme === "dark") return { themeName: "dark" };
    // "light" explicitly chosen — plain light, no default brandkit
    return { themeName: "light" };
  }

  // Element is "default" or unset — check game default
  if (gameDefaultTheme && gameDefaultTheme !== "default") {
    if (!BUILTIN_THEMES.includes(gameDefaultTheme))
      return { brandkitId: gameDefaultTheme, themeName: "light" };
    if (gameDefaultTheme === "dark") return { themeName: "dark" };
    // Game says "light" but element said "default" — fall through to
    // division defaults so the default brandkit still applies
  }

  // Division defaults
  if (defaultBrandkitId) {
    return { brandkitId: defaultBrandkitId, themeName: "light" };
  }

  if (preferredColorTheme === "dark") {
    return { themeName: "dark" };
  }

  return { themeName: "light" };
}
